// Wave 14 — Power vs. CdA scatter.
//
// Plots every time-series sample where both power and CdA exist, colored
// by position class. X axis: power (W). Y axis: CdA (m²), inverted so
// "lower is better" reads as upward on the plot. Linear regression line
// fitted closed-form across all points; the rider's single best (CdA,
// power) point gets a brand-colored ring marker.

import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { colors } from '../theme';
import { positionColor } from '../services/positionColors';

export interface PowerCdaSample {
  cda: number;
  power_w: number;
  position: string | null;
  sessionId: string;
}

interface Props {
  samples: PowerCdaSample[];
  height?: number;
}

/** Closed-form least-squares fit y = a + b*x. Returns null on degenerate
 *  inputs (fewer than 2 points, or zero X-variance). */
function linearFit(points: Array<{ x: number; y: number }>): { a: number; b: number } | null {
  if (points.length < 2) return null;
  const n = points.length;
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
    sxx += p.x * p.x;
    sxy += p.x * p.y;
  }
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const b = (n * sxy - sx * sy) / denom;
  const a = (sy - b * sx) / n;
  return { a, b };
}

export default function PowerCdaScatter({ samples, height = 320 }: Props) {
  // Group samples by position for color-coded series.
  const seriesByPosition = useMemo(() => {
    const map = new Map<string, PowerCdaSample[]>();
    for (const s of samples) {
      const key = s.position ?? 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return Array.from(map.entries()).map(([position, points]) => ({
      id: `pos-${position}`,
      label: position,
      color: positionColor(position),
      data: points.map((p, idx) => ({
        x: p.power_w,
        y: p.cda,
        id: `${position}-${idx}`,
      })),
    }));
  }, [samples]);

  const fit = useMemo(
    () =>
      linearFit(
        samples.map((s) => ({ x: s.power_w, y: s.cda })),
      ),
    [samples],
  );

  // Best (lowest) CdA sample — surfaced in the small callout above the chart.
  const best = useMemo(() => {
    let best: PowerCdaSample | null = null;
    for (const s of samples) {
      if (!best || s.cda < best.cda) best = s;
    }
    return best;
  }, [samples]);

  // Bounds for the regression line. Drawn as a thin two-point series
  // overlaid on the scatter. The X domain spans the actual sample range
  // so the line doesn't extrapolate visually past where data exists.
  const regressionSeries = useMemo(() => {
    if (!fit || samples.length === 0) return null;
    const minP = Math.min(...samples.map((s) => s.power_w));
    const maxP = Math.max(...samples.map((s) => s.power_w));
    return {
      id: 'regression',
      label: 'Fit',
      color: colors.textMuted,
      data: [
        { x: minP, y: fit.a + fit.b * minP, id: 'fit-lo' },
        { x: maxP, y: fit.a + fit.b * maxP, id: 'fit-hi' },
      ],
    };
  }, [fit, samples]);

  if (samples.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontSize: '0.875rem', color: colors.textMuted }}>
            Pair a power meter to see your Power × CdA chart.
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint }}>
            Each session with paired power data plots one point per second here.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {best && (
        <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint, mb: 1 }}>
          Best point ·{' '}
          <Box component="span" sx={{ color: colors.brand, fontFamily: '"JetBrains Mono", monospace' }}>
            {best.cda.toFixed(3)} m²
          </Box>{' '}
          at{' '}
          <Box component="span" sx={{ color: colors.text, fontFamily: '"JetBrains Mono", monospace' }}>
            {best.power_w.toFixed(0)} W
          </Box>
          {best.position && (
            <>
              {' · '}
              <Box component="span" sx={{ color: positionColor(best.position) }}>
                {best.position}
              </Box>
            </>
          )}
        </Typography>
      )}
      <Box sx={{ width: '100%', height }}>
        <ScatterChart
          height={height}
          xAxis={[{ label: 'Power (W)' }]}
          yAxis={[{ label: 'CdA (m²)', reverse: true }]}
          series={[
            ...seriesByPosition.map((s) => ({
              type: 'scatter' as const,
              id: s.id,
              label: s.label,
              color: s.color,
              data: s.data,
              markerSize: 4,
            })),
            ...(regressionSeries
              ? [{
                  type: 'scatter' as const,
                  id: regressionSeries.id,
                  label: regressionSeries.label,
                  color: regressionSeries.color,
                  data: regressionSeries.data,
                  markerSize: 1,
                }]
              : []),
          ]}
        />
      </Box>
    </Box>
  );
}
